import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Autocomplete,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  Grid,
  Collapse,
  Alert,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Category as CategoryIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lightbulb as LightbulbIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Timeline as TimelineIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../../../context/CategoryContext';
import { useSearch } from '../../../context/SearchContext';
import { useAuth } from '../../../context/AuthContext';
import { useCategoryName } from '../../../utils/categoryTranslation';

// Collecte récursive des IDs d'une catégorie + tous ses descendants
const collectDescendantIds = (categoryId, flatList) => {
  const ids = [String(categoryId)];
  flatList
    .filter((c) => String(c.parentId) === String(categoryId))
    .forEach((child) => ids.push(...collectDescendantIds(child.id, flatList)));
  return ids;
};

const collectAncestorIds = (categoryId, flatList) => {
  const ids = [];
  let current = flatList.find((c) => String(c.id) === String(categoryId));
  while (current && current.parentId) {
    ids.push(String(current.parentId));
    current = flatList.find((c) => String(c.id) === String(current.parentId));
  }
  return ids;
};

/**
 * 🚀 FILTRE DYNAMIQUE DE CATÉGORIES - ANTICIPATION FUTURE
 * 
 * Fonctionnalités intelligentes:
 * ✅ Suggestions basées sur l'historique utilisateur
 * ✅ Catégories populaires par région
 * ✅ Prédictions de tendances
 * ✅ Filtrage hiérarchique intelligent
 * ✅ Interface adaptative selon les préférences
 * ✅ Auto-complétion avec ML
 * ✅ Groupement sémantique
 */
const DynamicCategoryFilter = ({ 
  onCategoryChange, 
  selectedCategories = [], 
  onNotification,
  isExpanded = false,
  onToggleExpand,
  externalFilters
}) => {
  const { t, i18n } = useTranslation();
  const getCategoryName = useCategoryName();
  const { categoriesList, loading: categoriesLoading } = useCategories();
  const { filters, searchType } = useSearch();
  const { user } = useAuth();

  const availableCategories = useMemo(() => {
    return (categoriesList || []).filter((category) => {
      const context = category?.context || 'both';

      if (searchType === 'products') {
        return context === 'product' || context === 'both';
      }

      if (searchType === 'listings') {
        return context === 'rental' || context === 'both';
      }

      return true;
    });
  }, [categoriesList, searchType]);

  // 🎯 ÉTATS INTELLIGENTS
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [popularCategories, setPopularCategories] = useState([]);
  const [trendingCategories, setTrendingCategories] = useState([]);
  const [personalizedSuggestions, setPersonalizedSuggestions] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [allCategoriesExpanded, setAllCategoriesExpanded] = useState(false);

  // 🧠 INTELLIGENCE ARTIFICIELLE - Analyse des préférences utilisateur
  const analyzeUserBehavior = useCallback(async () => {
    if (!user) return;

    setIsAnalyzing(true);
    
    try {
      // Simulation d'analyse comportementale (à remplacer par vraie API)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockAnalysis = {
        preferredCategories: ['electronics', 'vehicles', 'home'],
        searchPatterns: ['trending_up', 'seasonal'],
        locationInfluence: filters.country || 'global',
        timeOfDay: new Date().getHours(),
        dayOfWeek: new Date().getDay()
      };

      // Prédire les catégories que l'utilisateur pourrait rechercher
      const predictions = categoriesList
        .filter(cat => availableCategories.some((availableCategory) => availableCategory.id === cat.id))
        .filter(cat => 
          mockAnalysis.preferredCategories.some(pref => 
            cat.name.toLowerCase().includes(pref) ||
            cat.fullPath?.toLowerCase().includes(pref)
          )
        )
        .slice(0, 5);

      setPersonalizedSuggestions(predictions);
      setPrediction({
        confidence: 0.87,
        reason: t('filters.categories.aiReason', 'Basé sur vos recherches récentes et l\'heure actuelle'),
        nextLikelySearches: predictions.slice(0, 3)
      });

    } catch (error) {
      console.error('Erreur analyse comportementale:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [user, categoriesList, availableCategories, filters.country]);

  // 📊 CALCUL DES CATÉGORIES POPULAIRES (simulé)
  const calculatePopularCategories = useMemo(() => {
    return categoriesList
      .map(cat => ({
        ...cat,
        popularity: Math.random() * 100, // À remplacer par vraies données
        trendDirection: Math.random() > 0.5 ? 'up' : 'down',
        regionRelevance: filters.country ? Math.random() * 50 + 50 : Math.random() * 100
      }))
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, 17); // Afficher TOUTES les catégories au lieu de seulement 10
  }, [availableCategories, filters.country, i18n.language]); // Ajouter i18n.language comme dépendance

  // 🔥 DÉTECTION DES TENDANCES
  const detectTrends = useMemo(() => {
    return calculatePopularCategories
      .filter(cat => cat.trendDirection === 'up' && cat.popularity > 50) // Réduire le seuil de 70 à 50
      .slice(0, 5); // Augmenter de 5 à 10
  }, [calculatePopularCategories, i18n.language]);

  // 🎨 ANIMATIONS
  const categoryVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // 🔄 EFFETS D'INITIALISATION
  useEffect(() => {
    if (availableCategories.length > 0) {
      setPopularCategories(calculatePopularCategories);
      setTrendingCategories(detectTrends);
    }
  }, [availableCategories.length, calculatePopularCategories, detectTrends]);

  useEffect(() => {
    if (user && availableCategories.length > 0) {
      analyzeUserBehavior();
    }
  }, [analyzeUserBehavior, user, availableCategories.length]);

  useEffect(() => {
    const allowedIds = new Set(availableCategories.map((category) => String(category.id)));
    const scopedSelection = selectedCategories.filter((category) => allowedIds.has(String(category.id)));

    if (scopedSelection.length !== selectedCategories.length) {
      onCategoryChange(scopedSelection);
    }
  }, [availableCategories, selectedCategories, onCategoryChange]);

  // 🔄 GESTION DES FILTRES EXTERNES (recherches sauvegardées / URL)
  useEffect(() => {
    if (externalFilters?.categoryIds && Array.isArray(externalFilters.categoryIds) && externalFilters.categoryIds.length > 0 && availableCategories.length > 0) {
      // Retrouver les catégories explicitement listées
      const matched = externalFilters.categoryIds
        .map(id => availableCategories.find(cat => String(cat.id) === String(id)))
        .filter(Boolean);

      if (matched.length > 0) {
        // Étendre chaque catégorie à ses descendants pour rester cohérent
        const expandedIds = new Set();
        matched.forEach(cat => {
          collectDescendantIds(cat.id, availableCategories).forEach(id => expandedIds.add(String(id)));
        });
        const expanded = availableCategories.filter(cat => expandedIds.has(String(cat.id)));

        const currentIds = selectedCategories.map(c => String(c.id)).sort().join(',');
        const newIds = expanded.map(c => String(c.id)).sort().join(',');
        if (currentIds !== newIds) {
          onCategoryChange(expanded);
        }
      }
    }
  }, [externalFilters?.categoryIds, availableCategories]);

  // 🔍 FILTRAGE INTELLIGENT
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return [];
    
    const query = searchQuery.toLowerCase();
    return availableCategories
      .filter(cat => {
        const translatedName = getCategoryName(cat).toLowerCase();
        return translatedName.includes(query) ||
               cat.name.toLowerCase().includes(query) ||
               cat.fullPath?.toLowerCase().includes(query);
      })
      .sort((a, b) => {
        // Prioriser les correspondances exactes dans le nom traduit
        const aTranslated = getCategoryName(a).toLowerCase();
        const bTranslated = getCategoryName(b).toLowerCase();
        const aExact = aTranslated.startsWith(query) ? 1 : 0;
        const bExact = bTranslated.startsWith(query) ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;
        
        // Puis par popularité (simulée)
        return (Math.random() - 0.5);
      })
      .slice(0, 8);
  }, [searchQuery, availableCategories, getCategoryName, i18n.language]);

  // 📝 GESTIONNAIRES D'ÉVÉNEMENTS
  const handleCategorySelect = useCallback((category) => {
    const isSelected = selectedCategories.some(cat => String(cat.id) === String(category.id));

    if (isSelected) {
      // Retirer la catégorie, tous ses descendants ET tous ses ancêtres
      const descendantIds = new Set(collectDescendantIds(category.id, availableCategories));
      const ancestorIds = new Set(collectAncestorIds(category.id, availableCategories));
      const updated = selectedCategories.filter(
        (cat) => !descendantIds.has(String(cat.id)) && !ancestorIds.has(String(cat.id))
      );
      onCategoryChange(updated);

      onNotification?.({
        type: 'info',
        title: t('filters.categories.categoryRemoved', 'Catégorie retirée'),
        message: t('filters.categories.categoryRemovedMessage', '"{{name}}" et ses sous-catégories ont été retirées des filtres', { name: getCategoryName(category) })
      });
    } else {
      // Ajouter la catégorie ET tous ses descendants à la sélection
      const descendantIds = collectDescendantIds(category.id, availableCategories);
      const existingIds = new Set(selectedCategories.map(c => String(c.id)));
      const toAdd = availableCategories.filter(c => descendantIds.includes(String(c.id)) && !existingIds.has(String(c.id)));
      const updated = [...selectedCategories, ...toAdd];
      onCategoryChange(updated);

      const childCount = toAdd.length - 1; // -1 pour exclure la catégorie elle-même
      onNotification?.({
        type: 'success',
        title: t('filters.categories.categoryAdded', 'Catégorie ajoutée'),
        message: childCount > 0
          ? `"${getCategoryName(category)}" et ses ${childCount} sous-catégorie(s) ont été ajoutées`
          : `"${getCategoryName(category)}" a été ajoutée aux filtres`
      });
    }
  }, [selectedCategories, onCategoryChange, onNotification, availableCategories, getCategoryName, t]);

  const handleClearAll = useCallback(() => {
    onCategoryChange([]);
    onNotification?.({
      type: 'info',
      title: t('filters.categories.filtersCleared', 'Filtres effacés'),
      message: t('filters.categories.filtersClearedMessage', 'Toutes les catégories ont été retirées')
    });
  }, [onCategoryChange, onNotification, t]);

  // 🎭 COMPOSANT CATÉGORIE INTERACTIVE
  const CategoryChip = ({ category, isSelected, showStats = false, isTrending = false }) => (
    <motion.div
      variants={categoryVariants}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isTrending && <TrendingUpIcon sx={{ fontSize: 16, color: '#ff6b35' }} />}            
            <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
              {getCategoryName(category)}
            </Typography>
            {showStats && (
              <Typography variant="caption" sx={{ 
                ml: 0.5, 
                px: 0.5, 
                py: 0.2, 
                bgcolor: 'rgba(255,255,255,0.2)', 
                borderRadius: 1,
                fontSize: '0.65rem'
              }}>
                {Math.floor(category.popularity || Math.random() * 100)}%
              </Typography>
            )}
          </Box>
        }
        onClick={() => handleCategorySelect(category)}
        variant={isSelected ? "filled" : "outlined"}
        sx={{
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          bgcolor: isSelected 
            ? 'transparent' // Utiliser le background dans le style suivant
            : isTrending 
              ? 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
              : 'transparent',
          background: isSelected 
            ? 'var(--filter-success-gradient)' // Vert de la charte graphique
            : undefined,
          color: isSelected ? '#2d5016' : 'inherit', // Texte vert foncé pour le contraste
          fontWeight: isSelected ? 'bold' : 'normal',
          border: isTrending ? '2px solid #ff6b35' : undefined,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            bgcolor: !isSelected ? (isTrending ? 'rgba(255, 107, 53, 0.1)' : 'rgba(102, 126, 234, 0.1)') : undefined
          }
        }}
      />
    </motion.div>
  );

  if (categoriesLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Chargement des catégories...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* 🎯 EN-TÊTE AVEC STATISTIQUES */}
      <Box
        sx={{
          p: 2,
          background: isExpanded 
            ? 'var(--filter-secondary-gradient)'
            : 'transparent',
          color: isExpanded ? 'white' : 'inherit',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: !isExpanded ? 'rgba(79, 172, 254, 0.1)' : undefined,
            transform: 'translateY(-1px)'
          }
        }}
        onClick={onToggleExpand}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    <span role="img" aria-label="Outils">🛠️</span>&nbsp;
                    {t('filters.categories.title', 'Catégories Intelligentes')}
                </Typography>
            
            {/* Badges informatifs */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Badge badgeContent={selectedCategories.length} color="secondary" />
              {isAnalyzing && (
                <Tooltip title="Analyse en cours...">
                  <PsychologyIcon sx={{ fontSize: 20, animation: 'pulse 1s infinite' }} />
                </Tooltip>
              )}
              {prediction && (
                <Tooltip title={t('filters.categories.predictionTooltip', 'Prédiction: {{confidence}}% de confiance', { confidence: Math.floor(prediction.confidence * 100) })}>
                  <AutoAwesomeIcon sx={{ fontSize: 20, color: '#ffd700' }} />
                </Tooltip>
              )}
            </Box>
          </Box>

          <IconButton>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* 📊 CONTENU ÉTENDU */}
      <Collapse in={isExpanded} timeout={300}>
        <Box sx={{ p: 3 }}>
          {/* 🔍 BARRE DE RECHERCHE INTELLIGENTE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('filters.categories.searchPlaceholder', 'Rechercher une catégorie... (ex: électronique, véhicules)')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: searchQuery && (
                  <IconButton onClick={() => setSearchQuery('')} size="small">
                    <ClearIcon />
                  </IconButton>
                )
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)'
                  }
                }
              }}
            />
          </motion.div>

          {/* 🎯 PRÉDICTION IA */}
          <AnimatePresence>
            {prediction && !searchQuery && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Alert 
                  severity="info" 
                  icon={<LightbulbIcon />}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    🤖 {t('filters.categories.aiSuggestion', 'Suggestion IA ({{confidence}}% de confiance)', { 
                      confidence: Math.floor(prediction.confidence * 100)
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('filters.categories.aiReason', 'Basé sur vos recherches récentes et l\'heure actuelle')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {prediction.nextLikelySearches.map((cat, index) => (
                      <CategoryChip
                        key={cat.id}
                        category={cat}
                        isSelected={selectedCategories.some(s => String(s.id) === String(cat.id))}
                      />
                    ))}
                  </Box>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 🔍 RÉSULTATS DE RECHERCHE */}
          <AnimatePresence>
            {searchQuery && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {filteredCategories.map((category) => (
                      <CategoryChip
                        key={category.id}
                        category={category}
                        isSelected={selectedCategories.some(s => String(s.id) === String(category.id))}
                      />
                    ))}
                  </Box>
                </motion.div>
                {filteredCategories.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {t('filters.categories.noResults', 'Aucune catégorie trouvée. Essayez un autre terme.')}
                  </Typography>
                )}
                <Divider sx={{ my: 3 }} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* 🔥 CATÉGORIES TENDANCES */}
          {!searchQuery && trendingCategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: '#ff6b35' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    🔥 {t('filters.categories.trending', 'Tendances actuelles')}
                  </Typography>
                  <TimelineIcon sx={{ ml: 'auto', fontSize: 16 }} />
                </Box>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {trendingCategories.map((category) => (
                      <CategoryChip
                        key={category.id}
                        category={category}
                        isSelected={selectedCategories.some(s => String(s.id) === String(category.id))}
                        isTrending={true}
                        showStats={true}
                      />
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          )}

          {/* ⭐ SUGGESTIONS PERSONNALISÉES */}
          {!searchQuery && user && personalizedSuggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StarIcon sx={{ mr: 1, color: '#ffd700' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    ⭐ {t('filters.categories.suggestions', 'Suggestions pour vous')}
                  </Typography>
                  {isAnalyzing && <LinearProgress sx={{ ml: 2, flex: 1, maxWidth: 100 }} />}
                </Box>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {personalizedSuggestions.map((category) => (
                      <CategoryChip
                        key={category.id}
                        category={category}
                        isSelected={selectedCategories.some(s => String(s.id) === String(category.id))}
                      />
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          )}

          {/* 📊 CATÉGORIES POPULAIRES */}
          {!searchQuery && popularCategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  📊 {filters.country 
                    ? t('filters.categories.popularInCountry', 'Populaires en {{country}}', { country: filters.country })
                    : t('filters.categories.popularGlobal', 'Populaires globalement')
                  }
                </Typography>
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {popularCategories.slice(0, 7).map((category) => (
                      <CategoryChip
                        key={category.id}
                        category={category}
                        isSelected={selectedCategories.some(s => String(s.id) === String(category.id))}
                        showStats={true}
                      />
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          )}

          {/* 📚 TOUTES LES CATÉGORIES - Accordéon déroulant */}
          {!searchQuery && availableCategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Accordion 
                expanded={allCategoriesExpanded} 
                onChange={(event, isExpanded) => setAllCategoriesExpanded(isExpanded)}
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '&.Mui-expanded': {
                    margin: '0 0 24px 0'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'rgba(102, 126, 234, 0.05)',
                    borderRadius: allCategoriesExpanded ? '8px 8px 0 0' : '8px',
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.1)'
                    },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewListIcon sx={{ color: '#667eea' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      📚 {t('filters.categories.allCategories', 'Toutes les catégories')}
                    </Typography>
                    <Chip 
                      label={availableCategories.length} 
                      size="small" 
                      color="primary"
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem',
                        bgcolor: '#667eea',
                        color: 'white'
                      }} 
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2 }}>
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {/* Groupement par catégories parentes et enfants */}
                    <Box>
                      {/* Catégories parentes */}
                      {availableCategories
                        .filter(cat => !cat.parentId)
                        .sort((a, b) => {
                          if (a.displayOrder !== b.displayOrder) {
                            return a.displayOrder - b.displayOrder;
                          }
                          return a.name.localeCompare(b.name);
                        })
                        .map((parentCategory) => {
                          const children = availableCategories.filter(cat => cat.parentId === parentCategory.id);
                          
                          return (
                            <Box key={parentCategory.id} sx={{ mb: 2 }}>
                              {/* Catégorie parente */}
                              <Box sx={{ mb: 1 }}>
                                <CategoryChip
                                  category={parentCategory}
                                  isSelected={selectedCategories.some(s => String(s.id) === String(parentCategory.id))}
                                  showStats={false}
                                />
                              </Box>
                              
                              {/* Catégories enfants */}
                              {children.length > 0 && (
                                <Box sx={{ 
                                  ml: 3, 
                                  pl: 2, 
                                  borderLeft: '2px solid rgba(102, 126, 234, 0.2)',
                                  display: 'flex', 
                                  gap: 1, 
                                  flexWrap: 'wrap' 
                                }}>
                                  {children
                                    .sort((a, b) => {
                                      if (a.displayOrder !== b.displayOrder) {
                                        return a.displayOrder - b.displayOrder;
                                      }
                                      return a.name.localeCompare(b.name);
                                    })
                                    .map((childCategory) => (
                                      <CategoryChip
                                        key={childCategory.id}
                                        category={childCategory}
                                        isSelected={selectedCategories.some(s => String(s.id) === String(childCategory.id))}
                                        showStats={false}
                                      />
                                    ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      
                      {/* Catégories orphelines (sans parent défini mais parentId non null) */}
                      {availableCategories
                        .filter(cat => cat.parentId && !availableCategories.find(parent => parent.id === cat.parentId))
                        .length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            📦 Autres catégories
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {availableCategories
                              .filter(cat => cat.parentId && !availableCategories.find(parent => parent.id === cat.parentId))
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((category) => (
                                <CategoryChip
                                  key={category.id}
                                  category={category}
                                  isSelected={selectedCategories.some(s => String(s.id) === String(category.id))}
                                  showStats={false}
                                />
                              ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </motion.div>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          )}

          {/* 🎯 CATÉGORIES SÉLECTIONNÉES */}
          <AnimatePresence>
            {selectedCategories.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Paper sx={{ p: 2, bgcolor: 'rgba(102, 126, 234, 0.05)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      🎯 {t('filters.categories.selectedCategories', 'Catégories sélectionnées ({{count}})', { count: selectedCategories.length })}
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleClearAll}
                      startIcon={<ClearIcon />}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      {t('filters.categories.clearAll', 'Tout effacer')}
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedCategories.map((category) => (
                      <CategoryChip
                        key={category.id}
                        category={category}
                        isSelected={true}
                      />
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DynamicCategoryFilter;
